/**
 * Spire-safe SSO login_hint: replace RedirectToExternalProvider in the blueprint
 * so Azure B2C email prefill works in sandbox (client-framework changes are not in Spire build-base).
 *
 * Also preserves Sign In ?returnUrl=... after SSO (e.g. cart/checkout → sign-in → back to cart),
 * even when the vanity Sign In path differs from the CMS SignInPage URL.
 */
import { sanitizePostAuthReturnUrl } from "@iewcasia/Utilities/sanitizePostAuthReturnUrl";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    chain,
    RedirectToExternalProvider,
} from "@insite/client-framework/Store/Context/Handlers/SignInExternalProvider";
import { getLocation, removeAbsoluteUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

/** Reads the SSO email field (or ?username= deep-link) for Azure B2C login_hint. */
const getSsoEmailHint = (): string | undefined => {
    const field =
        (document.getElementById("ssoUserName") as HTMLInputElement | null) ??
        (document.querySelector('[data-test-selector="signInExternal_email"]') as HTMLInputElement | null);

    const fieldValue = field?.value?.trim();
    if (fieldValue && fieldValue.indexOf("@") > 0) {
        return fieldValue;
    }

    // Auto-SSO from Sign In ?username=... may fire before the input is committed.
    const usernameFromQuery = parseQueryString<{ username?: string }>(window.location.search).username?.trim();
    if (usernameFromQuery && usernameFromQuery.indexOf("@") > 0) {
        return usernameFromQuery;
    }

    return undefined;
};

/** Appends login_hint to the Identity Server authorize URL (query + acr_values). */
const appendLoginHint = (url: string, loginHint: string): string => {
    const encodedHint = encodeURIComponent(loginHint);
    let redirectUrl = url;

    const separator = redirectUrl.indexOf("?") > -1 ? "&" : "?";
    redirectUrl = `${redirectUrl}${separator}login_hint=${encodedHint}`;

    if (/acr_values=([^&]*)/i.test(redirectUrl)) {
        redirectUrl = redirectUrl.replace(
            /acr_values=([^&]*)/i,
            (_, acrValues) => `acr_values=${acrValues}+login_hint%3a${encodedHint}`,
        );
    }

    return redirectUrl;
};

/**
 * Resolves where to send the user after SSO.
 * Prefer explicit ?returnUrl= on the Sign In page (checkout/cart/RFQ flows).
 */
const resolvePostSsoReturnUrl = (pathname: string, search: string, signInPageUrl?: string): string => {
    const queryReturnUrl = parseQueryString<{ returnUrl?: string }>(search).returnUrl;
    if (queryReturnUrl) {
        return removeAbsoluteUrl(queryReturnUrl) || "/";
    }

    const signInPath = signInPageUrl ? removeAbsoluteUrl(signInPageUrl).toLowerCase() : "";
    const currentPath = pathname.toLowerCase();
    const onSignInPage = !!signInPath && currentPath === signInPath;

    // Already on Sign In without returnUrl → home. Otherwise return to the current page.
    if (onSignInPage) {
        return "/";
    }

    return `${pathname}${search}` || "/";
};

/** Injects returnUrl into Identity external-provider URL (acr_values returnurl:...). */
const injectReturnUrl = (providerUrl: string, encodedReturnUrl: string): string => {
    if (/returnurl%3a/i.test(providerUrl)) {
        return providerUrl.replace(/returnurl%3a[^+\s&]*/i, `returnurl%3a${encodedReturnUrl}`);
    }

    return providerUrl.replace(/returnurl%3a%2f/i, `returnurl%3a${encodedReturnUrl}`);
};

replaceInChain(chain, RedirectToExternalProvider, ({ parameter: { externalProvider }, getState }) => {
    if (!externalProvider.url) {
        return;
    }

    const location = getLocation(getState());
    const signInUrl = getPageLinkByPageType(getState(), "SignInPage")?.url;

    let returnUrl = sanitizePostAuthReturnUrl(
        resolvePostSsoReturnUrl(location.pathname, location.search, signInUrl),
    ) || "/";
    // OOTB always appends forcereturnurl; skip it for bare "/" so Dashboard Is Homepage still wins.
    const returnPathOnly = (returnUrl.split("?")[0] || "/").replace(/\/$/, "") || "";
    if (returnPathOnly !== "" && returnPathOnly !== "/") {
        returnUrl = `${returnUrl + (returnUrl.indexOf("?") > -1 ? "&" : "?")}forcereturnurl`;
    }
    returnUrl = encodeURIComponent(returnUrl);

    let redirectUrl = injectReturnUrl(externalProvider.url, returnUrl);

    const loginHint = getSsoEmailHint();
    if (loginHint) {
        redirectUrl = appendLoginHint(redirectUrl, loginHint);
    }

    window.location.href = redirectUrl;
});
